import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { formatAmountWithCommas } from "../../Utils/utils";

export const InputField = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  required = false,
  type = "text",
  placeholder = "",
  labelCol = "col-md-2",
  inputCol = "col-md-3",
  stacked = false,
  wrapperCol = "",
  // Multiple fields
  fields = [],
  defaultValue = "",
  children,
  ...rest
}) => {
  const isMultiple = fields.length > 0;

  const fieldMarkup = isMultiple ? (
    <div className="d-flex ml-md-3 mr-md-3">
      {fields.map((field, index) => (
        <div key={index} className="position-relative ">
          <input
            type={field.type || "text"}
            name={field.name}
            value={field.value}
            disabled={field.disabled || false}
            placeholder={field.placeholder || ""}
            className="input_styling"
            onChange={field.onChange}
            {...field.rest}
          />
        </div>
      ))}
    </div>
  ) : (
    <div className="position-relative">
      <input
        type={type}
        name={name}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        className="input_styling ml-md-3"
        onChange={onChange}
        defaultValue={defaultValue}
        {...rest}
      />
      {children}
    </div>
  );

  if (stacked) {
    return (
      <div className={`${wrapperCol} stacked-form-field mb-3`}>
        <label className="input_label d-block m-0 mb-2">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
        <div>{fieldMarkup}</div>
      </div>
    );
  }

  return (
    <>
      <div className={`${labelCol} mt-4 text-left p-0`}>
        <label className="input_label m-0">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
      </div>
      <div className={`${inputCol} mt-4 p-0`}>{fieldMarkup}</div>
    </>
  );
};

export const SelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  required = false,
  labelCol = "col-md-2",
  inputCol = "col-md-3",
  stacked = false,
  wrapperCol = "",
  children,
  ...rest
}) => {
  const selectMarkup = (
    <select
      name={name}
      value={value}
      disabled={disabled}
      className="input_styling ml-md-3"
      onChange={onChange}
      {...rest}
    >
      {options.map(({ value, label, disabled, ...restOption }, index) => (
        <option key={index} value={value} disabled={disabled} {...restOption}>
          {label}
        </option>
      ))}
    </select>
  );

  if (stacked) {
    return (
      <div className={`${wrapperCol} stacked-form-field mb-3`}>
        <label className="input_label d-block m-0 mb-2">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
        <div className="position-relative">
          {selectMarkup}
          {children}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`${labelCol} mt-4 text-left p-0`}>
        <label className="input_label m-0">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
      </div>

      <div className={`${inputCol} mt-4 p-0 position-relative`}>
        {selectMarkup}
        {children}
      </div>
    </>
  );
};

export const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = "",
  rows = 3,
  labelCol = "col-md-2",
  inputCol = "col-md-3",
  stacked = false,
  wrapperCol = "",
  defaultValue = "",
  ...rest
}) => {
  const textAreaMarkup = (
    <textarea
      name={name}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      rows={rows}
      className="input_styling ml-md-3 input_address"
      style={{ height: "100px" }}
      onChange={onChange}
      defaultValue={defaultValue}
      {...rest}
    />
  );

  if (stacked) {
    return (
      <div className={`${wrapperCol} stacked-form-field mb-3`}>
        <label className="input_label d-block m-0 mb-2">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
        <div className="position-relative">{textAreaMarkup}</div>
      </div>
    );
  }

  return (
    <>
      <div className={`${labelCol} mt-4 text-left p-0`}>
        <label className="input_label m-0">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
      </div>

      <div className={`${inputCol} mt-4 p-0 position-relative`}>{textAreaMarkup}</div>
    </>
  );
};

export const SearchableSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = "",
  disabled = false,
  required = false,
  hideAsterisk = false,
  clearLegacySelect = false,
  className = "input_styling w-100 m-0",
  wrapperClassName = "",
  style = {},
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const displayValue = clearLegacySelect && value === "Select" ? "" : value;

  useEffect(() => {
    if (!isOpen || displayValue === "") {
      const selectedOption = options.find((opt) => opt.value === displayValue);
      setSearchTerm(selectedOption ? selectedOption.label : displayValue || "");
    }
  }, [displayValue, isOpen, options]);

  const updateCoords = () => {
    if (containerRef.current) {
      const inputEl = containerRef.current.querySelector("input");
      if (inputEl) {
        const inputRect = inputEl.getBoundingClientRect();
        setCoords({
          top: inputRect.bottom,
          left: inputRect.left,
          width: inputRect.width,
        });
      } else {
        const rect = containerRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateCoords();
      window.addEventListener("scroll", updateCoords, true);
      window.addEventListener("resize", updateCoords);
    }
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setIsOpen(true);
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelect = (option) => {
    setSearchTerm(option.label);
    setIsOpen(false);
    if (onChange) {
      onChange({
        target: {
          name,
          value: option.value,
        },
      });
    }

    // Dispatch native event so useFormAlert can detect this programmatic change.
    // We dispatch it on the container div rather than the input to ensure React's
    // synthetic onChange (which opens the dropdown) doesn't accidentally refire.
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }, 0);
  };

  const filteredOptions = options.filter((option) =>
    String(option.label || "")
      .toLowerCase()
      .includes(String(searchTerm || "").toLowerCase()),
  );

  return (
    <div
      className={`position-relative ${wrapperClassName}`}
      ref={containerRef}
      style={style}
      onBlur={(e) => {
        const isInsideContainer = containerRef.current?.contains(e.relatedTarget);
        const isInsideDropdown = dropdownRef.current?.contains(e.relatedTarget);
        if (!isInsideContainer && !isInsideDropdown) {
          setIsOpen(false);
          const selectedOption = options.find((opt) => opt.value === value);
          setSearchTerm(selectedOption ? selectedOption.label : value || "");
        }
      }}
    >
      <div className="position-relative w-100">
        <input
          type="text"
          name={name}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          autoComplete="off"
          style={{ width: "100%" }}
          {...rest}
        />
        <span
          className="position-absolute"
          style={{
            right: "6px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FaChevronDown size={10} />
        </span>

        {required && !hideAsterisk && <span className="estaric">*</span>}
      </div>

      {isOpen &&
        !disabled &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: `${coords.top + 4}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              zIndex: 99999,
              maxHeight: "250px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                  className="custom-dropdown-item d-flex flex-column align-items-start"
                  style={{
                    cursor: "pointer",
                    fontSize: "13px",
                    padding: "4px 12px",
                    gap: "2px",
                  }}
                >
                  <span style={{ fontWeight: 400, color: "#282c30ff" }}>{option.label}</span>
                  {option.status && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        fontWeight: 400,
                      }}
                    >
                      {option.status}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="text-muted text-center" style={{ fontSize: "13px", padding: "12px" }}>
                No results found
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export const SearchableSelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "",
  disabled = false,
  required = false,
  labelCol = "col-md-2",
  inputCol = "col-md-3",
  ...rest
}) => {
  return (
    <>
      <div className={`${labelCol} mt-4 text-left p-0`}>
        <label className="input_label m-0">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
      </div>

      <div className={`${inputCol} mt-4 p-0 position-relative`}>
        <SearchableSelect
          name={name}
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          hideAsterisk={true}
          // className="input_styling w-100 m-0 ml-md-3"
          wrapperClassName="ml-md-3 d-inline-block"
          style={{ width: "85%", verticalAlign: "middle" }}
          {...rest}
        />
      </div>
    </>
  );
};

export const AmountInput = ({
  name,
  value,
  onChange,
  onBlur,
  disabled = false,
  readOnly = false,
  className = "input_styling",
  style = {},
  ...rest
}) => {
  const handleChange = (e) => {
    const formatted = formatAmountWithCommas(e.target.value);
    e.target.value = formatted;
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <input
      type="text"
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      className={className}
      style={style}
      autoComplete="off"
      {...rest}
    />
  );
};

export const AmountInputField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  labelCol = "col-md-2",
  inputCol = "col-md-3",
  ...rest
}) => {
  return (
    <>
      <div className={`${labelCol} mt-4 text-left p-0`}>
        <label className="input_label m-0">
          {label}
          {required && <span className="estaric text-danger ml-1">*</span>}
        </label>
      </div>

      <div className={`${inputCol} mt-4 p-0 position-relative`}>
        <AmountInput
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          className="input_styling ml-md-3"
          {...rest}
        />
      </div>
    </>
  );
};
